import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Table, Pagination, Input, Space, DatePicker, Select, Modal, Spin } from "antd";
import { SearchOutlined, CalendarOutlined, ReconciliationOutlined, UndoOutlined, ClearOutlined, DownloadOutlined, FilterOutlined } from "@ant-design/icons";
import ReactDatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { ConfigProvider } from "antd";
import type { InputRef } from "antd";
import ptBR from "antd/lib/locale/pt_BR";
import * as S from "./styles";
import { getConciliadorBancarioByGroup, conciliarManual, desfazerConciliacaoManual, ConciliadorBancarioFiltro } from "../../service/ConciliadorBancarioService";
import api from "../../service/api";
import { DataItem, TableOptions, defaultOptions } from "./types";
import { buildColumns } from "./columns";
import ModalDesfazerConciliacao from "./ModalDesfazerConciliacao";
import ModalAjustes from "./ModalAjustes";

const PAGE_SIZE = 50;

const TabelaConciliadorBancario: React.FC<{ currentUser: any }> = (_props) => {
  const { RangePicker } = DatePicker;
  const searchInputRef = React.useRef<InputRef | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [pageData, setPageData] = useState<DataItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [grupoDoCliente, setGrupoDoCliente] = useState<string | null>(null);
  const [options, setOptions] = useState<TableOptions>(defaultOptions);
  const [dataRange, setDataRange] = useState<[any, any] | null>(null);
  const [administradora, setAdministradora] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState<string>("");
  const [isConciliando, setIsConciliando] = useState(false);
  const [isDesfazerOpen, setIsDesfazerOpen] = useState(false);
  const [isDesfazendo, setIsDesfazendo] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isProcessando, setIsProcessando] = useState(false);
  const [ajustesRow, setAjustesRow] = useState<DataItem | null>(null);

  // Monitora as linhas da tabela na tela para inferir a administradora dinamicamente
  useEffect(() => {
    if (pageData && pageData.length > 0) {
      // Captura a adquirente do primeiro item (pode estar como adquirente ou Adquirente no seu tipo)
      const primeiraAdquirente = pageData[0].adquirente || (pageData[0] as any).Adquirente;
      
      if (primeiraAdquirente) {
        // Verifica se todas as linhas visíveis na tela possuem exatamente o mesmo valor
        const todasIguais = pageData.every(d => {
          const current = d.adquirente || (d as any).Adquirente;
          return current === primeiraAdquirente;
        });

        if (todasIguais) {
          setAdministradora(primeiraAdquirente);
        } else {
          setAdministradora(null); // Se houver adquirentes mistas na tela, limpa para segurança
        }
      }
    } else {
      setAdministradora(null); // Sem dados na tela, limpa
    }
  }, [pageData]);

  const buildFiltro = useCallback((page: number): ConciliadorBancarioFiltro => {
    const toDateStr = (v: any): string | undefined => {
      if (!v) return undefined;
      const d: Date = v?.toDate ? v.toDate() : new Date(v);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const getFilter = (key: string): string | undefined => {
      const val = columnFilters[key] || columnFilters[key.charAt(0).toUpperCase() + key.slice(1)];
      if (!val || (Array.isArray(val) && val.length === 0)) return undefined;
      return Array.isArray(val) ? val.join(',') : String(val);
    };

    const datePgtoCol = columnFilters["dataPgto"] || columnFilters["DataPgto"];
    let dataInicio: string | undefined;
    let dataFim: string | undefined;
    if (datePgtoCol && Array.isArray(datePgtoCol) && Array.isArray(datePgtoCol[0])) {
      dataInicio = toDateStr(datePgtoCol[0][0]);
      dataFim    = toDateStr(datePgtoCol[0][1]);
    } else if (dataRange?.[0] || dataRange?.[1]) {
      dataInicio = toDateStr(dataRange?.[0]);
      dataFim    = toDateStr(dataRange?.[1]);
    }

    return {
      StartIndex: page,
      PageSize:   PAGE_SIZE,
      Empresa:            getFilter("empresa"),
      Cnpj:               getFilter("cnpj"),
      Adquirente:         getFilter("adquirente"),
      Bandeira:           getFilter("bandeira"),
      Banco:              getFilter("banco"),
      Agencia:            getFilter("agencia"),
      Conta:              getFilter("conta"),
      StatusPgto:         getFilter("statusPgto"),
      StatusConciliacao:  getFilter("statusConciliacao"),
      Justificativa:      getFilter("justificativa"),
      DataPgtoInicio:     dataInicio,
      DataPgtoFim:        dataFim,
    };
  }, [columnFilters, dataRange]);

  const fetchData = useCallback(async (page = 1) => {
    if (!grupoDoCliente) return;
    setIsLoading(true);
    try {
      const filtro = buildFiltro(page);
      const response = await getConciliadorBancarioByGroup(grupoDoCliente, filtro);

      setPageData(response.result ?? []);
      setTotalItems(response.totalCount ?? 0);
      setCurrentPage(page);

      const hasOptions =
        response.statusPgtoOpcoes?.length ||
        response.adquirentes?.length ||
        response.empresas?.length;
      if (hasOptions) {
        setOptions({
          statusPgtoOpcoes:        response.statusPgtoOpcoes        ?? [],
          statusConciliacaoOpcoes: response.statusConciliacaoOpcoes ?? [],
          adquirentes:             response.adquirentes             ?? [],
          bandeiras:               response.bandeiras               ?? [],
          bancos:                  response.bancos                  ?? [],
          agencias:                response.agencias                ?? [],
          contas:                  response.contas                  ?? [],
          empresas:                response.empresas                ?? [],
          cnpjs:                   response.cnpjs                   ?? [],
        });
      }
    } catch (err: any) {
      const detail = err?.response?.data;
      console.error("Erro ao carregar conciliador bancário:", detail ?? err);
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.title ?? "Erro ao carregar dados."
      );
    } finally {
      setIsLoading(false);
    }
  }, [grupoDoCliente, buildFiltro]);

  useEffect(() => {
    const cliente = localStorage.getItem("cliente");
    if (cliente) {
      const obj = JSON.parse(cliente);
      if (obj?.nomeDoGrupo) setGrupoDoCliente(obj.nomeDoGrupo);
    }
  }, []);

  useEffect(() => {
    if (grupoDoCliente) fetchData(1);
  }, [grupoDoCliente, columnFilters, dataRange, fetchData]);

  const totals = useMemo(() => {
    const toNum = (val: string) => parseFloat(val?.replace(/\./g, "").replace(",", ".")) || 0;
    return {
      valorPrevisto:   pageData.reduce((sum, d) => sum + toNum(d.valorPrevisto), 0),
      valorDepositado: pageData.reduce((sum, d) => sum + toNum(d.valorDepositado), 0),
      diferenca:       pageData.reduce((sum, d) => sum + toNum(d.diferenca), 0),
      ajustes:         pageData.reduce((sum, d) => sum + toNum(d.ajustes), 0),
    };
  }, [pageData]);

  const handleProcessar = useCallback(async () => {
    if (!grupoDoCliente) return;

    const toDateStr = (v: any): string | undefined => {
      if (!v) return undefined;
      const d: Date = v?.toDate ? v.toDate() : new Date(v);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const dataInicio = toDateStr(dataRange?.[0]);
    const dataFim = toDateStr(dataRange?.[1]);

    if (!dataInicio || !dataFim) {
      toast.warning('Selecione o período (Data pgto de/até) antes de conciliar.');
      return;
    }

    setIsProcessando(true);
    try {
      await api.post(`/ConciliadorBancario/processar`, {
        grupo: grupoDoCliente,
        dataInicio,
        dataFim,
      });
      toast.success("Conciliação processada com sucesso!");
      fetchData();
    } catch (err: any) {
      const detail = err?.response?.data;
      toast.error(typeof detail === "string" ? detail : detail?.message ?? "Erro ao processar conciliação.");
    } finally {
      setIsProcessando(false);
    }
  }, [grupoDoCliente, dataRange, fetchData]);

  const handleConciliarManual = useCallback(async () => {
    if (!grupoDoCliente) return;
    if (!administradora) {
      toast.warning("Selecione a administradora antes de conciliar.");
      return;
    }
    if (!justificativa.trim()) {
      toast.warning("Informe a justificativa antes de conciliar.");
      return;
    }
    const filtro = buildFiltro(currentPage);
    if (!filtro.DataPgtoInicio || !filtro.DataPgtoFim) {
      toast.warning("Selecione o período de Data Pgto antes de conciliar.");
      return;
    }
    setIsConciliando(true);
    try {
      const result = await conciliarManual(grupoDoCliente, administradora, justificativa, filtro);
      if (result.success) {
        toast.success(result.message || "Conciliação manual realizada com sucesso.");
        setJustificativa("");
        fetchData();
      } else {
        toast.error(result.message || "Erro ao realizar conciliação manual.");
      }
    } catch (err: any) {
      const detail = err?.response?.data;
      toast.error(typeof detail === "string" ? detail : detail?.message ?? "Erro ao realizar conciliação manual.");
    } finally {
      setIsConciliando(false);
    }
  }, [grupoDoCliente, administradora, justificativa, buildFiltro, currentPage, fetchData]);

  const hasActiveFilter = useMemo(() => {
    const hasColumnFilter = Object.values(columnFilters).some(
      v => v && (Array.isArray(v) ? v.length > 0 : true)
    );
    const hasDateFilter = !!(dataRange?.[0] || dataRange?.[1]);
    return hasColumnFilter || hasDateFilter;
  }, [columnFilters, dataRange]);

  const handleDesfazerConciliacao = useCallback(async () => {
    if (!grupoDoCliente) return;
    if (!administradora) {
      toast.warning("Selecione a administradora antes de desfazer a conciliação.");
      setIsDesfazerOpen(false);
      return;
    }
    const filtro = buildFiltro(currentPage);
    if (!filtro.DataPgtoInicio || !filtro.DataPgtoFim) {
      toast.warning("Selecione o período de Data Pgto antes de desfazer a conciliação.");
      setIsDesfazerOpen(false);
      return;
    }
    setIsDesfazendo(true);
    try {
      const result = await desfazerConciliacaoManual(grupoDoCliente, administradora, filtro);
      if (result.success) {
        toast.success(result.message || "Conciliação desfeita com sucesso.");
        setIsDesfazerOpen(false);
        fetchData();
      } else {
        toast.error(result.message || "Erro ao desfazer conciliação.");
      }
    } catch (err: any) {
      const detail = err?.response?.data;
      toast.error(typeof detail === "string" ? detail : detail?.message ?? "Erro ao desfazer conciliação.");
    } finally {
      setIsDesfazendo(false);
    }
  }, [grupoDoCliente, administradora, buildFiltro, currentPage, fetchData]);

  const handleDownloadCSV = useCallback(async () => {
    if (!grupoDoCliente) return;
    setIsDownloading(true);
    try {
      const fmtDate = (d: Date | null) =>
        d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

      const getFilter = (key: string): string | undefined => {
        const val = columnFilters[key] || columnFilters[key.charAt(0).toUpperCase() + key.slice(1)];
        if (!val || (Array.isArray(val) && val.length === 0)) return undefined;
        return Array.isArray(val) ? val.join(',') : String(val);
      };

      const datePgto: [Date | null, Date | null] = (() => {
        const col = columnFilters["dataPgto"] || columnFilters["DataPgto"];
        if (col && Array.isArray(col) && Array.isArray(col[0])) {
          const [s, e] = col[0];
          return [s ?? null, e ?? null] as [Date | null, Date | null];
        }
        if (dataRange) return [dataRange[0] ?? null, dataRange[1] ?? null] as [Date | null, Date | null];
        return [null, null] as [Date | null, Date | null];
      })();

      const params: Record<string, string> = {};
      const empresa = getFilter("empresa");           if (empresa)    params["Empresa"]           = empresa;
      const cnpj = getFilter("cnpj");                 if (cnpj)       params["Cnpj"]              = cnpj;
      const adquirente = getFilter("adquirente");     if (adquirente) params["Adquirente"]         = adquirente;
      const bandeira = getFilter("bandeira");         if (bandeira)   params["Bandeira"]          = bandeira;
      const statusPgto = getFilter("statusPgto");     if (statusPgto) params["StatusPgto"]        = statusPgto;
      const statusConc = getFilter("statusConciliacao"); if (statusConc) params["StatusConciliacao"] = statusConc;
      const ini = fmtDate(datePgto[0]);               if (ini)        params["DataPgtoInicio"]     = ini;
      const fim = fmtDate(datePgto[1]);               if (fim)        params["DataPgtoFim"]        = fim;

      const response = await api.get(
        `/ConciliadorBancarioExport/download-csv-conciliador-bancario/${encodeURIComponent(grupoDoCliente)}`,
        { responseType: 'blob', params }
      );

      const grupoLimpo = grupoDoCliente.replace(/\s+/g, '_');
      const fileName = `Conciliacao_Bancaria_${grupoLimpo}.csv`;
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download concluído!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar arquivo para download.");
    } finally {
      setIsDownloading(false);
    }
  }, [grupoDoCliente, columnFilters, dataRange]);

  const handleFiltersChange = useCallback((_: any, filters: Record<string, any>) => {
    setColumnFilters(filters);
    setCurrentPage(1);
  }, []);

  const getColumnSearchProps = useCallback((_dataIndex: string, title: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Buscar ${title}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} size="small">Buscar</Button>
          <Button onClick={() => { clearFilters(); confirm(); }} size="small">Limpar</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ fontSize: 18, color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) setTimeout(() => searchInputRef.current?.focus(), 100);
    },
  }), [searchInputRef]);

  const getColumnCheckboxProps = useCallback((options: string[]) => {
    const sorted = [...options].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    return ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8, minWidth: 160 }}>
        <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
          {sorted.map(opt => (
            <div
              key={opt}
              style={{ padding: '4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => {
                const current: string[] = Array.isArray(selectedKeys) ? selectedKeys : [];
                const next = current.includes(opt)
                  ? current.filter(k => k !== opt)
                  : [...current, opt];
                setSelectedKeys(next);
              }}
            >
              <input
                type="checkbox"
                readOnly
                checked={Array.isArray(selectedKeys) && selectedKeys.includes(opt)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13 }}>{opt}</span>
            </div>
          ))}
        </div>
        <Space>
          <Button type="primary" size="small" onClick={() => confirm()}>OK</Button>
          <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Limpar</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ fontSize: 18, color: filtered ? "#1677ff" : undefined }} />
    ),
  });
  }, []);

  const getColumnDateProps = useCallback((_dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 4, width: "fit-content", maxWidth: 250 }}>
        <ReactDatePicker
          selectsRange
          inline
          startDate={selectedKeys[0]?.[0] || null}
          endDate={selectedKeys[0]?.[1] || null}
          onChange={(update: [Date | null, Date | null]) => {
            const [start, end] = update || [null, null];
            setSelectedKeys(start ? [[start, end ?? null]] : []);
          }}
          locale="pt-BR"
        />
        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          <Button
            type="primary"
            size="small"
            style={{ flex: 1 }}
            onClick={() => {
              const [start, end] = selectedKeys[0] || [null, null];
              if (start) setSelectedKeys([[start, end ?? start]]);
              confirm();
            }}
          >
            Buscar
          </Button>
          <Button
            size="small"
            danger
            style={{ flex: 1 }}
            onClick={() => { clearFilters?.(); confirm(); }}
          >
            Limpar
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ fontSize: 18, color: filtered ? "#1890ff" : undefined }} />
    ),
  }), []);

  const columns = useMemo(
    () => buildColumns(options, getColumnSearchProps, getColumnDateProps, getColumnCheckboxProps, setAjustesRow),
    [options, getColumnSearchProps, getColumnDateProps, getColumnCheckboxProps]
  );

  return (
    <S.Container>
      <ModalDesfazerConciliacao
        open={isDesfazerOpen}
        count={totalItems}
        hasFilter={hasActiveFilter}
        isLoading={isDesfazendo}
        onClose={() => setIsDesfazerOpen(false)}
        onConfirm={handleDesfazerConciliacao}
      />

      <ModalAjustes
        open={!!ajustesRow}
        onClose={() => setAjustesRow(null)}
        adquirente={ajustesRow?.adquirente ?? null}
        cnpj={ajustesRow?.cnpj ?? null}
        dataPgto={ajustesRow?.dataPgto ?? null}
      />

      <Modal
        open={isProcessando}
        footer={null}
        closable={false}
        centered
        width={300}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "16px 0" }}>
          <Spin size="large" />
          <span style={{ fontSize: 15, fontWeight: 600, color: "#444" }}>Processando conciliação...</span>
        </div>
      </Modal>
      <S.DivTable>
        <S.Tools>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <ConfigProvider locale={ptBR}>
              <RangePicker
                format="DD/MM/YYYY"
                value={dataRange}
                onChange={dates => { setDataRange(dates as [any, any] | null); setCurrentPage(1); }}
                placeholder={["Data pgto de", "Data pgto até"]}
                style={{ width: 280 }}
                allowClear
              />
            </ConfigProvider>
            <Button
              type="primary"
              icon={<ReconciliationOutlined />}
              onClick={handleProcessar}
              loading={isProcessando}
              style={{ background: "#27ae60", display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              CONCILIAR
            </Button>
            <Button
              danger
              icon={<ClearOutlined />}
              onClick={() => {
                setColumnFilters({});
                setDataRange(null);
                setCurrentPage(1);
                setTableKey(k => k + 1);
              }}
              style={{ fontWeight: 'bold' }}
            >
              LIMPAR FILTROS
            </Button>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <Select
              placeholder="Administradora"
              value={administradora}
              onChange={setAdministradora}
              allowClear
              style={{ width: 200 }}
            >
              {options.adquirentes.map(a => (
                <Select.Option key={a} value={a}>{a}</Select.Option>
              ))}
            </Select>
            <Input
              placeholder="Justificativa"
              value={justificativa}
              onChange={e => setJustificativa(e.target.value)}
              style={{ width: 220 }}
            />
            <Button
              type="primary"
              icon={<ReconciliationOutlined />}
              onClick={handleConciliarManual}
              loading={isConciliando}
              style={{ background: "#e67e22", borderColor: "#e67e22", display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              CONCILIAÇÃO MANUAL
            </Button>
            <Button
              danger
              icon={<UndoOutlined />}
              onClick={() => {
                if (!administradora) {
                  toast.warning("Selecione a administradora antes de desfazer a conciliação.");
                  return;
                }
                setIsDesfazerOpen(true);
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              DESFAZER CONCILIAÇÃO
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadCSV}
              loading={isDownloading}
              style={{ background: "#27ae60", borderColor: "#27ae60", color: "#fff", display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              DOWNLOAD CSV
            </Button>
          </div>
        </S.Tools>

        <Table
          key={tableKey}
          columns={columns}
          dataSource={pageData}
          loading={isLoading}
          rowKey="id"
          pagination={false}
          scroll={{ x: "max-content" }}
          onChange={handleFiltersChange}
          summary={() => {
            const fmt = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            const cell = (v: number, colored?: boolean) => (
              <div style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: colored && v < 0 ? "#e74c3c" : undefined }}>
                {fmt(v)}
              </div>
            );
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} />
                <Table.Summary.Cell index={5}>{cell(totals.valorPrevisto)}</Table.Summary.Cell>
                <Table.Summary.Cell index={6}>{cell(totals.valorDepositado)}</Table.Summary.Cell>
                <Table.Summary.Cell index={7}>{cell(totals.diferenca, true)}</Table.Summary.Cell>
                <Table.Summary.Cell index={8} colSpan={6} />
                <Table.Summary.Cell index={14}>{cell(totals.ajustes)}</Table.Summary.Cell>
                <Table.Summary.Cell index={15} />
              </Table.Summary.Row>
            );
          }}
        />

        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalItems}
            onChange={p => fetchData(p)}
            showSizeChanger={false}
          />
        </div>
      </S.DivTable>
    </S.Container>
  );
};

export default TabelaConciliadorBancario;